import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import StarsOutlinedIcon from "@mui/icons-material/StarsOutlined";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "../../api/apiFetch";
import { getSprintWorkspacePath, startSprint } from "../../api/trustbuds";
import { ACTION_MESSAGE_PARTNER, resolveWebAction } from "../../navigation/actionIntents";
import { trustBudTokens } from "../../theme/tokens";
import LoadingState from "../common/LoadingState";
import FindPeopleTab from "./find/FindPeopleTab";
import RequestsTab from "./RequestsTab";
import SprintsTab from "./SprintsTab";

const TAB_ITEMS = [
  { key: "circle", label: "My people" },
  { key: "sprints", label: "Boards" },
  { key: "requests", label: "Requests" },
  { key: "find", label: "Find people" },
];

const FILTER_OPTIONS = [
  { key: "all", label: "All" },
  { key: "active_sprint", label: "In Accountability" },
  { key: "active_week", label: "Active this week" },
  { key: "idle", label: "Idle" },
];

const SORT_OPTIONS = [
  { key: "recent", label: "Recently active" },
  { key: "active_days", label: "Most active this week" },
  { key: "quiet_longest", label: "Quiet longest" },
  { key: "name", label: "Name" },
];

const REVIEW_RATINGS = [
  {
    value: "very_supportive",
    label: "Very supportive",
    body: "They helped me stay with it.",
  },
  {
    value: "supportive",
    label: "Supportive",
    body: "This was useful overall.",
  },
  {
    value: "neutral",
    label: "Neutral",
    body: "It was okay, but not especially helpful.",
  },
  {
    value: "not_supportive",
    label: "Not supportive",
    body: "This did not feel like the right fit.",
  },
];

function dayDiff(timestamp) {
  if (!timestamp) return null;
  const time = new Date(timestamp).getTime();
  if (Number.isNaN(time)) return null;
  return Math.max(0, Math.floor((Date.now() - time) / 86400000));
}

function buildMessagePath(connection) {
  return resolveWebAction(ACTION_MESSAGE_PARTNER, {
    partnerName: connection?.name,
    partnerUserId: connection?.userId,
    sprintId: connection?.sprintId,
    connectionId: connection?.connectionId,
    source: "connections",
  }).route;
}

function buildConnectionModel(connection, context) {
  const days = dayDiff(context?.last_activity_timestamp || connection?.last_activity_at);
  const connectionState = connection?.connection_state || null;
  const state = connectionState?.state || connection?.state || "";
  const hasActiveSprint = state === "ACTIVE_SPRINT" || Boolean(connection?.active_sprint?.id);
  const statusKey = String(connection?.activity_status || context?.status || "").trim() || "idle";
  const statusLabel = String(connection?.status_label || context?.status_label || "").trim() || "Unavailable";
  const statusDetail =
    String(
      connection?.status_detail ||
        context?.status_detail ||
        context?.light_context?.last_activity_label ||
        ""
    ).trim() || "Status unavailable";
  const statusColor =
    statusKey === "active" ? "#16a34a" : statusKey === "quiet" ? "#ef4444" : "#f59e0b";
  const dotColor =
    statusKey === "active" ? "#1fb66a" : statusKey === "quiet" ? "#ef4444" : "#f59e0b";
  const activeDaysThisWeek = Number(context?.light_context?.active_days_this_week || 0);
  const relationshipKind = context?.relationship_state?.kind || (hasActiveSprint ? "active_sprint" : "connected_no_sprint");
  const sprintContext = connection?.active_sprint_context || null;
  const sprintFocusTitle = String(sprintContext?.focus_title || "").trim();
  const sprintFocusSummary = String(sprintContext?.focus_summary || "").trim();

  const sharedGoalTitle = String(context?.shared_goal_summary?.title || "").trim();
  const sharedGoalSummary = String(context?.shared_goal_summary?.summary || "").trim();
  const focusArea = String(context?.focus_area || "").trim();
  const sharedFocusStatement = String(context?.shared_focus_statement || "").trim();
  const headline = String(context?.headline || "").trim();
  const defaultPrivateTitle =
    hasActiveSprint || relationshipKind === "active_sprint"
      ? "Accountability details are private"
      : "Focus is private";
  const defaultPrivateSubtitle =
    hasActiveSprint || relationshipKind === "active_sprint"
      ? "They have not shared more here yet."
      : relationshipKind === "previous_sprint"
        ? "Start again to pick this back up."
        : "Start Accountability to make this feel real.";

  const resolvedGoalTitle =
    (hasActiveSprint ? sprintFocusTitle : "") ||
    sharedGoalTitle ||
    focusArea ||
    headline ||
    defaultPrivateTitle;
  const resolvedGoalSubtitle =
    (hasActiveSprint ? sprintFocusSummary : "") ||
    sharedGoalSummary ||
    sharedFocusStatement ||
    (headline && headline !== resolvedGoalTitle ? headline : "") ||
    defaultPrivateSubtitle;

  return {
    id: String(connection?.id),
    connectionId: connection?.id,
    userId: context?.user_id || connection?.other_user?.id || null,
    sprintId: connection?.active_sprint?.id || null,
    activeSprint: connection?.active_sprint || null,
    latestSprint: connection?.latest_sprint || null,
    sprintCount: Number(connection?.sprint_count || 0),
    connectionState,
    state,
    behaviorSummary: connection?.behavior_summary || null,
    sprintBehavior: connection?.behavior_summary?.sprint || null,
    needsRenewal: Boolean(connection?.needs_renewal),
    viewerReviewed: Boolean(connection?.viewer_reviewed),
    canRestart: Boolean(connection?.can_restart),
    viewerRequestedRenewal: Boolean(connection?.viewer_requested_renewal),
    otherRequestedRenewal: Boolean(connection?.other_requested_renewal),
    canRenew: Boolean(connection?.can_renew),
    canClose: Boolean(connection?.can_close),
    daysRemaining: Number.isFinite(Number(connection?.days_remaining)) ? Number(connection?.days_remaining) : null,
    name: context?.name || connection?.other_user?.username || "Connection",
    avatar: context?.avatar || connection?.other_user?.profile_image || null,
    goalTitle: resolvedGoalTitle,
    goalSubtitle: resolvedGoalSubtitle,
    daysSinceMove: days,
    activeDaysThisWeek,
    sprintBadge: hasActiveSprint ? "In Accountability" : state === "PAST_SPRINT" ? "Past Accountability" : "Not in Accountability",
    hasActiveSprint,
    healthPriority: hasActiveSprint ? -1 : days ?? 0,
    statusKey,
    statusLabel,
    statusColor,
    dotColor,
    statusDetail,
    quietLabel: statusKey === "quiet" ? statusDetail : "",
    lastActivityLabel: statusDetail,
  };
}

function cardShellSx(extra = {}) {
  return {
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    border: "1px solid rgba(231, 238, 246, 0.98)",
    boxShadow: "0 10px 28px rgba(15, 23, 42, 0.045)",
    ...extra,
  };
}

function SideNoteCard({ icon, title, body, cta, onClick }) {
  return (
    <Box sx={cardShellSx({ p: 2.1 })}>
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: "8px",
          display: "grid",
          placeItems: "center",
          backgroundColor: "#eef5ff",
          color: trustBudTokens.colors.brand.navy700,
          mb: 1.4,
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontSize: "1.35rem", fontWeight: 850, letterSpacing: 0, color: "#102033" }}>
        {title}
      </Typography>
      <Typography sx={{ mt: 0.55, fontSize: "0.98rem", lineHeight: 1.55, color: "#576b84" }}>
        {body}
      </Typography>
      <ButtonBase onClick={onClick} sx={{ mt: 1.4, color: "#3f49f0", fontWeight: 700, fontSize: "0.98rem", gap: 0.45 }}>
        {cta}
        <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
      </ButtonBase>
    </Box>
  );
}

function SprintReviewDialog({
  connection,
  rating,
  saving,
  error,
  onSelectRating,
  onSubmit,
  onClose,
}) {
  const partnerName = connection?.name || "this person";

  return (
    <Dialog
      open={Boolean(connection)}
      onClose={saving ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          border: "1px solid rgba(215, 226, 238, 0.94)",
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.18)",
        },
      }}
    >
      <Box sx={{ p: 2.4 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.2 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "8px",
              display: "grid",
              placeItems: "center",
              backgroundColor: "#eef5ff",
              color: trustBudTokens.colors.brand.navy700,
            }}
          >
            <StarsOutlinedIcon sx={{ fontSize: 21 }} />
          </Box>
          <IconButton disabled={saving} onClick={onClose} sx={{ color: "#60738c" }}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        <Typography sx={{ mt: 1.8, fontSize: "1.55rem", lineHeight: 1.14, fontWeight: 800, color: "#13233d" }}>
          Review your Accountability with {partnerName}
        </Typography>
        <Typography sx={{ mt: 0.8, color: "#576b84", lineHeight: 1.5 }}>
          Rate the experience so TrustBud can understand which accountability loops are actually helping.
        </Typography>

        <Box sx={{ mt: 1.8, display: "grid", gap: 0.85 }}>
          {REVIEW_RATINGS.map((option) => {
            const selected = rating === option.value;
            return (
              <ButtonBase
                key={option.value}
                onClick={() => onSelectRating(option.value)}
                sx={{
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: "18px minmax(0, 1fr)",
                  gap: 1,
                  alignItems: "flex-start",
                  textAlign: "left",
                  p: 1.25,
                  borderRadius: "8px",
                  border: "1px solid",
                  borderColor: selected ? "rgba(23, 52, 93, 0.28)" : "rgba(223, 232, 241, 0.95)",
                  backgroundColor: selected ? "#eef5ff" : "#ffffff",
                  transition: "background-color 140ms ease, border-color 140ms ease",
                  "&:hover": {
                    backgroundColor: selected ? "#e5efff" : "#f8fbff",
                  },
                }}
              >
                <Box
                  sx={{
                    mt: 0.2,
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    border: "2px solid",
                    borderColor: selected ? trustBudTokens.colors.brand.navy700 : "#b8c7d8",
                    backgroundColor: selected ? trustBudTokens.colors.brand.navy700 : "transparent",
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, color: "#13233d", lineHeight: 1.25 }}>
                    {option.label}
                  </Typography>
                  <Typography sx={{ mt: 0.25, fontSize: "0.92rem", color: "#60738c", lineHeight: 1.35 }}>
                    {option.body}
                  </Typography>
                </Box>
              </ButtonBase>
            );
          })}
        </Box>

        {error ? (
          <Typography sx={{ mt: 1.2, color: "#b42318", fontWeight: 700, fontSize: "0.92rem" }}>
            {error}
          </Typography>
        ) : null}

        <Button
          variant="contained"
          disabled={saving}
          onClick={onSubmit}
          sx={{
            mt: 1.7,
            width: "100%",
            minHeight: 46,
            borderRadius: "8px",
            backgroundColor: trustBudTokens.colors.brand.navy700,
            fontWeight: 800,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: trustBudTokens.colors.brand.navy800,
            },
          }}
        >
          {saving ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Save review"}
        </Button>
        <Button
          disabled={saving}
          onClick={onClose}
          sx={{
            mt: 0.8,
            width: "100%",
            minHeight: 40,
            borderRadius: "8px",
            color: "#60738c",
            fontWeight: 800,
            textTransform: "none",
          }}
        >
          Not now
        </Button>
      </Box>
    </Dialog>
  );
}

function RowBadge({ label, active }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 34,
        px: 1.45,
        borderRadius: "12px",
        backgroundColor: active ? "#e8f8ef" : "#eef5ff",
        color: active ? "#18a35d" : trustBudTokens.colors.brand.navy700,
        fontSize: "0.98rem",
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
}

function challengeModeLabel(mode) {
  return "Accountability";
}

function challengeFocusPreview(connection) {
  return String(connection?.goalTitle || connection?.goalSubtitle || "General consistency").trim();
}

function isUsefulChallengeFocus(label) {
  const normalized = String(label || "").trim().toLowerCase();
  return Boolean(
    normalized &&
      !["focus is private", "accountability details are private", "challenge details are private", "general consistency"].includes(normalized)
  );
}

async function loadChallengeFocusOptions(connection) {
  try {
    const dashboard = await apiFetch("/dashboard/");
    const vision = dashboard?.vision || null;
    let goals = Array.isArray(vision?.goals) ? vision.goals : [];
    if (vision?.id) {
      try {
        const series = await apiFetch(`/dashboard/vision/${vision.id}/data/`);
        if (Array.isArray(series?.goals) && series.goals.length) {
          goals = series.goals;
        }
      } catch {
        // The dashboard summary still gives us enough to use the fallback below.
      }
    }
    const options = goals
      .filter((goal) => !goal?.completed_at)
      .map((goal) => {
        const habits = Array.isArray(goal?.commitments)
          ? goal.commitments.filter((habit) => habit?.is_active !== false)
          : [];
        return {
          focus_type: "goal",
          goal_id: Number(goal.id),
          habit_ids: habits.map((habit) => Number(habit.id)).filter((id) => Number.isFinite(id)),
          habit_titles: habits.map((habit) => String(habit?.title || "").trim()).filter(Boolean),
          vision_id: vision?.id ? Number(vision.id) : null,
          display_label: String(goal?.title || "Goal").trim(),
          visibility: "partner",
        };
      })
      .filter((option) => option.display_label && Number.isFinite(Number(option.goal_id)));
    if (options.length) return options;
  } catch {
    // Fall through to row context fallback.
  }

  const suggested = challengeFocusPreview(connection);
  if (isUsefulChallengeFocus(suggested)) {
    return [
      {
        focus_type: "goal",
        goal_id: null,
        habit_ids: [],
        habit_titles: [],
        vision_id: null,
        display_label: suggested,
        visibility: "partner",
      },
    ];
  }
  return [];
}

async function chooseChallengeFocus(connection) {
  const options = await loadChallengeFocusOptions(connection);
  if (!options.length) {
    return { focus_type: "general", display_label: "General consistency", visibility: "partner" };
  }
  const menu = options
    .slice(0, 9)
    .map((option, index) => `${index + 1}. ${option.display_label}`)
    .join("\n");
  const answer = window.prompt(
    `What is this Accountability about?\n\n${menu}\n0. General consistency\n\nEnter a number.`,
    "1"
  );
  if (answer === null) return null;
  const selected = Number(answer);
  if (selected === 0) {
    return { focus_type: "general", display_label: "General consistency", visibility: "partner" };
  }
  const focus = options[selected - 1] || options[0];
  const habitTitles = Array.isArray(focus?.habit_titles) ? focus.habit_titles : [];
  const habitIds = Array.isArray(focus?.habit_ids) ? focus.habit_ids : [];
  if (habitTitles.length <= 1 || habitIds.length <= 1) {
    return focus;
  }
  const habitMenu = habitTitles
    .slice(0, 9)
    .map((title, index) => `${index + 1}. ${title}`)
    .join("\n");
  const habitAnswer = window.prompt(
    `Which habits should count?\n\n0. All active habits\n${habitMenu}\n\nEnter a number.`,
    "0"
  );
  if (habitAnswer === null) return null;
  const habitIndex = Number(habitAnswer);
  if (habitIndex === 0) return focus;
  const selectedHabitId = habitIds[habitIndex - 1];
  const selectedHabitTitle = habitTitles[habitIndex - 1];
  if (!selectedHabitId || !selectedHabitTitle) return focus;
  return {
    ...focus,
    habit_ids: [selectedHabitId],
    habit_titles: [selectedHabitTitle],
  };
}

function confirmChallengeRequest(connection, sprintMode, durationDays, challengeFocus) {
  const modeLabel = challengeModeLabel(sprintMode);
  const habitLine = challengeFocus?.habit_titles?.length
    ? `\nCounts: ${challengeFocus.habit_titles.slice(0, 3).join(" · ")}${challengeFocus.habit_titles.length > 3 ? " · +more" : ""}`
    : "";
  return window.confirm(
    `Send ${modeLabel} invite to ${connection.name}?\n\nThey will see this request before accepting.\n\nFocus: ${challengeFocus.display_label}${habitLine}\nDuration: ${durationDays} days`
  );
}

function ChallengeStyleDialog({ open, onClose, connections = [], sendingConnectionId = null, onSend }) {
  const [durationDays, setDurationDays] = useState(7);
  const [step, setStep] = useState("duration");
  const [mode, setMode] = useState("support");
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [focusOptions, setFocusOptions] = useState([]);
  const [selectedFocus, setSelectedFocus] = useState(null);
  const [loadingFocus, setLoadingFocus] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep("duration");
    setMode("support");
    setDurationDays(7);
    setSelectedConnection(null);
    setFocusOptions([]);
    setSelectedFocus(null);
    setLoadingFocus(false);
  }, [open]);

  async function selectConnection(connection) {
    setSelectedConnection(connection);
    setStep("focus");
    setLoadingFocus(true);
    const options = await loadChallengeFocusOptions(connection);
    setFocusOptions(options);
    setLoadingFocus(false);
  }

  function chooseFocus(focus) {
    setSelectedFocus(focus);
    const titles = focus?.habit_titles || [];
    const ids = focus?.habit_ids || [];
    setStep(titles.length > 1 && ids.length > 1 ? "habits" : "review");
  }

  function chooseSingleHabit(title, id) {
    if (!selectedFocus) return;
    setSelectedFocus({
      ...selectedFocus,
      habit_ids: id ? [id] : [],
      habit_titles: [title],
    });
    setStep("review");
  }

  const habitTitles = selectedFocus?.habit_titles || [];
  const canGoBack = step !== "duration";
  const optionButtonSx = {
    justifyContent: "flex-start",
    borderRadius: "8px",
    py: 1.2,
    px: 1.45,
    textTransform: "none",
    fontWeight: 800,
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: "8px", width: "min(440px, calc(100vw - 32px))" } }}>
      <Box sx={{ p: 3, display: "grid", gap: 1.6 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {canGoBack ? (
            <Button
              onClick={() => {
                if (step === "person") setStep("duration");
                else if (step === "focus") setStep("person");
                else if (step === "habits") setStep("focus");
                else setStep(habitTitles.length > 1 ? "habits" : "focus");
              }}
              sx={{ textTransform: "none", fontWeight: 800 }}
            >
              Back
            </Button>
          ) : (
            <Box />
          )}
          <Typography sx={{ color: "#254b79", fontWeight: 900, fontSize: "0.78rem", textTransform: "uppercase" }}>
            Accountability setup
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseRoundedIcon />
          </IconButton>
        </Stack>

        {step === "style" ? (
          <>
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 850, color: "#102033", letterSpacing: 0 }}>
              Start Accountability
            </Typography>
            <Typography sx={{ color: "#50647e", lineHeight: 1.45 }}>
              Accountability is a Daily Report agreement with one TrustBud.
            </Typography>
            <Button variant="contained" onClick={() => { setMode("support"); setStep("duration"); }} sx={optionButtonSx}>
              Continue
            </Button>
          </>
        ) : null}

        {step === "duration" ? (
          <>
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 850, color: "#102033", letterSpacing: 0 }}>
              Choose duration
            </Typography>
            {[3, 7, 14].map((days) => (
              <Button
                key={days}
                variant={durationDays === days ? "contained" : "outlined"}
                onClick={() => {
                  setDurationDays(days);
                  setStep("person");
                }}
                sx={optionButtonSx}
              >
                {days} days
              </Button>
            ))}
          </>
        ) : null}

        {step === "person" ? (
          <>
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 850, color: "#102033", letterSpacing: 0 }}>
              Choose a TrustBud
            </Typography>
            <Box sx={{ display: "grid", gap: 1, maxHeight: 360, overflowY: "auto" }}>
              {connections.length ? connections.map((connection) => (
                <Button key={connection.id} variant="outlined" onClick={() => selectConnection(connection)} sx={optionButtonSx}>
                  <Box sx={{ textAlign: "left" }}>
                    <Typography sx={{ fontWeight: 900 }}>{connection.name}</Typography>
                    <Typography sx={{ fontSize: "0.86rem", color: "#60738c" }}>{connection.goalTitle || "General consistency"}</Typography>
                  </Box>
                </Button>
              )) : (
                <Typography sx={{ color: "#60738c" }}>No available TrustBuds right now.</Typography>
              )}
            </Box>
          </>
        ) : null}

        {step === "focus" ? (
          <>
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 850, color: "#102033", letterSpacing: 0 }}>
              What is this about?
            </Typography>
            <Typography sx={{ color: "#50647e", lineHeight: 1.45 }}>
              Choose what {selectedConnection?.name || "your TrustBud"} will see in the request.
            </Typography>
            {loadingFocus ? <CircularProgress size={24} /> : null}
            {!loadingFocus ? (
              <Box sx={{ display: "grid", gap: 1, maxHeight: 360, overflowY: "auto" }}>
                {focusOptions.slice(0, 6).map((focus) => (
                  <Button key={`${focus.focus_type}-${focus.goal_id || focus.display_label}`} variant="outlined" onClick={() => chooseFocus(focus)} sx={optionButtonSx}>
                    <Box sx={{ textAlign: "left" }}>
                      <Typography sx={{ fontWeight: 900 }}>{focus.display_label}</Typography>
                      <Typography sx={{ fontSize: "0.86rem", color: "#60738c" }}>
                        {focus.habit_titles?.length ? `${focus.habit_titles.length} habits can count` : "General accountability"}
                      </Typography>
                    </Box>
                  </Button>
                ))}
                <Button variant="outlined" onClick={() => chooseFocus({ focus_type: "general", display_label: "General consistency", visibility: "partner" })} sx={optionButtonSx}>
                  General consistency
                </Button>
              </Box>
            ) : null}
          </>
        ) : null}

        {step === "habits" && selectedFocus ? (
          <>
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 850, color: "#102033", letterSpacing: 0 }}>
              Which habits count?
            </Typography>
            <Button variant="outlined" onClick={() => setStep("review")} sx={optionButtonSx}>
              All active habits
            </Button>
            {habitTitles.slice(0, 6).map((title, index) => (
              <Button key={`${title}-${index}`} variant="outlined" onClick={() => chooseSingleHabit(title, selectedFocus.habit_ids?.[index])} sx={optionButtonSx}>
                {title}
              </Button>
            ))}
          </>
        ) : null}

        {step === "review" && selectedConnection && selectedFocus ? (
          <>
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 850, color: "#102033", letterSpacing: 0 }}>
              Review and send
            </Typography>
            <Box sx={{ p: 1.5, borderRadius: "8px", backgroundColor: "#f8fbff", border: "1px solid #e3eaf3", display: "grid", gap: 0.75 }}>
              <Typography>TrustBud: <strong>{selectedConnection.name}</strong></Typography>
              <Typography>Duration: <strong>{durationDays} days</strong></Typography>
              <Typography>Focus: <strong>{selectedFocus.display_label}</strong></Typography>
              <Typography>Counts: <strong>{habitTitles.length ? habitTitles.slice(0, 3).join(" · ") : "General consistency"}</strong></Typography>
            </Box>
            <Button
              variant="contained"
              disabled={sendingConnectionId === selectedConnection.connectionId}
              onClick={() => onSend?.(selectedConnection, mode, durationDays, selectedFocus)}
              sx={{ borderRadius: "8px", py: 1.2, textTransform: "none", fontWeight: 900 }}
            >
              {sendingConnectionId === selectedConnection.connectionId ? "Sending..." : "Send Accountability invite"}
            </Button>
          </>
        ) : null}
      </Box>
    </Dialog>
  );
}

function ConnectionsRow({ item, onOpenSprint, onMessage, onOpenProfile, onStartSprint, pendingChallengeMode, pendingChallengeDuration = 7 }) {
  const openProfile = () => onOpenProfile?.(item);
  const canSendPendingChallenge = Boolean(pendingChallengeMode && !item.hasActiveSprint && item.connectionId);
  const primaryAction = item.hasActiveSprint
    ? () => onOpenSprint?.(item)
    : canSendPendingChallenge
      ? () => onStartSprint?.(item, pendingChallengeMode, pendingChallengeDuration)
      : () => onStartSprint?.(item);
  const primaryLabel = item.hasActiveSprint
    ? "Open Accountability"
    : canSendPendingChallenge
      ? `Send ${pendingChallengeDuration}-day invite`
      : "Start Accountability";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "minmax(220px, 0.95fr) minmax(260px, 1fr) minmax(220px, auto)",
        },
        gap: { xs: 1.15, md: 1.55 },
        alignItems: { xs: "stretch", md: "center" },
        p: { xs: 1.15, md: 1.25 },
        mt: 1,
        borderRadius: "12px",
        border: "1px solid rgba(224, 233, 244, 0.98)",
        backgroundColor: "#fff",
        boxShadow: "0 12px 30px rgba(20, 42, 75, 0.045)",
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={openProfile}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openProfile();
          }
        }}
        sx={{
          display: "grid",
          gridTemplateColumns: "64px minmax(0, 1fr)",
          gap: 1.3,
          alignItems: "center",
          minWidth: 0,
          cursor: "pointer",
          borderRadius: "8px",
          px: 0.5,
          py: 0.35,
          mx: -0.5,
          my: -0.35,
          transition: "background-color 140ms ease, box-shadow 140ms ease",
          "&:hover": {
            backgroundColor: "rgba(23, 52, 93, 0.045)",
            boxShadow: "inset 0 0 0 1px rgba(23, 52, 93, 0.04)",
          },
          "&:focus-visible": {
            outline: "2px solid rgba(23, 52, 93, 0.18)",
            outlineOffset: 4,
          },
        }}
      >
        <Box sx={{ position: "relative", width: 56, height: 56 }}>
          <Avatar src={item.avatar || ""} sx={{ width: 56, height: 56, bgcolor: "#d6d1cb", fontSize: "1.4rem", fontWeight: 700 }}>
            {item.name?.slice(0, 1)?.toUpperCase()}
          </Avatar>
          <Box
            sx={{
              position: "absolute",
              right: -1,
              bottom: -1,
              width: 16,
              height: 16,
              borderRadius: "50%",
              backgroundColor: item.dotColor,
              border: "2px solid #fff",
            }}
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "1.18rem",
              fontWeight: 700,
              color: "#1b2843",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              "&:hover": { color: trustBudTokens.colors.brand.navy700, textDecoration: "underline" },
            }}
          >
            {item.name}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.65, mt: 0.5, minWidth: 0, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: "0.98rem", color: item.statusColor, fontWeight: 600 }}>
              {item.statusLabel}
            </Typography>
            <Typography sx={{ fontSize: "0.98rem", color: "#50647e" }}>
              •
            </Typography>
            <Typography sx={{ fontSize: "0.98rem", color: "#50647e" }}>
                  {item.lastActivityLabel}
            </Typography>
          </Box>
        </Box>
      </Box>

      <ButtonBase
        onClick={() => (item.hasActiveSprint ? onOpenSprint(item) : onMessage(item))}
        sx={{ justifyContent: "flex-start", textAlign: "left", borderRadius: "16px", minWidth: 0, px: 0.2 }}
      >
        <Box sx={{ display: "grid", gridTemplateColumns: "28px minmax(0, 1fr)", gap: 0.9, alignItems: "start", minWidth: 0 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              backgroundColor: "#eef5ff",
              color: trustBudTokens.colors.brand.navy700,
              mt: 0.1,
            }}
          >
            <TrackChangesRoundedIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "1.14rem",
                fontWeight: 700,
                color: "#1b2843",
                lineHeight: 1.24,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {item.goalTitle}
            </Typography>
            <Typography
              sx={{
                mt: 0.35,
                fontSize: "0.98rem",
                color: "#50647e",
                lineHeight: 1.3,
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {item.goalSubtitle}
            </Typography>
          </Box>
        </Box>
      </ButtonBase>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: { xs: "flex-start", md: "flex-end" },
          gap: 1,
          minWidth: 0,
          flexWrap: "wrap",
        }}
      >
        <RowBadge label={item.sprintBadge} active={item.hasActiveSprint} />
        <Typography
          sx={{
            fontSize: "0.92rem",
            color: "#50647e",
            lineHeight: 1.3,
            whiteSpace: "nowrap",
          }}
        >
          {item.activeDaysThisWeek} active day{item.activeDaysThisWeek === 1 ? "" : "s"}
        </Typography>
        <Button
          variant="outlined"
          onClick={primaryAction}
          sx={{
            minWidth: { xs: 0, md: 152 },
            width: { xs: "100%", sm: "auto" },
            minHeight: 42,
            borderRadius: "8px",
            borderColor: "rgba(95, 108, 255, 0.24)",
            color: "#3f49f0",
            fontSize: "0.96rem",
            fontWeight: 800,
            whiteSpace: "nowrap",
          }}
        >
          {primaryLabel}
        </Button>
        <IconButton sx={{ color: "#445776", display: { xs: "none", md: "inline-flex" } }}>
          <MoreHorizRoundedIcon />
        </IconButton>
      </Box>
    </Box>
  );
}

export default function ConnectionsScreen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterKey, setFilterKey] = useState("all");
  const [sortKey, setSortKey] = useState("recent");
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [sortAnchor, setSortAnchor] = useState(null);
  const [reviewConnection, setReviewConnection] = useState(null);
  const [reviewRating, setReviewRating] = useState("");
  const [reviewSaving, setReviewSaving] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const [pendingChallengeMode, setPendingChallengeMode] = useState(null);
  const [pendingChallengeDuration, setPendingChallengeDuration] = useState(7);
  const [challengeStyleOpen, setChallengeStyleOpen] = useState(false);
  const [startingConnectionId, setStartingConnectionId] = useState(null);
  const activeTab = searchParams.get("tab") === "find"
    ? "find"
    : searchParams.get("tab") === "requests"
      ? "requests"
      : searchParams.get("tab") === "sprints"
        ? "sprints"
        : "circle";

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const rawConnections = await apiFetch("/connections/").catch(() => []);
      const next = (Array.isArray(rawConnections) ? rawConnections : [])
        .map((connection) => buildConnectionModel(connection, connection?.row_context || null))
        .sort((left, right) => {
          if (left.hasActiveSprint !== right.hasActiveSprint) return left.hasActiveSprint ? -1 : 1;
          return (left.daysSinceMove ?? Number.MAX_SAFE_INTEGER) - (right.daysSinceMove ?? Number.MAX_SAFE_INTEGER);
        });

      setConnections(next);
    } catch {
      setConnections([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const data = await apiFetch("/companions/requests/");
      const incoming = Array.isArray(data?.incoming) ? data.incoming : [];
      const outgoing = Array.isArray(data?.outgoing) ? data.outgoing : [];
      setIncomingRequests(incoming);
      setOutgoingRequests(outgoing);
      window.dispatchEvent(
        new CustomEvent("trustbud:requests-unread", {
          detail: { count: incoming.length },
        })
      );
    } catch {
      setIncomingRequests([]);
      setOutgoingRequests([]);
      window.dispatchEvent(
        new CustomEvent("trustbud:requests-unread", {
          detail: { count: 0 },
        })
      );
    } finally {
      setRequestsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadRequests();
    const handler = () => loadData();
    window.addEventListener("trustbud:connections-changed", handler);
    return () => window.removeEventListener("trustbud:connections-changed", handler);
  }, [loadData, loadRequests]);

  const searchedConnections = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return connections;
    return connections.filter((item) => {
      const haystack = [
        item.name,
        item.goalTitle,
        item.goalSubtitle,
        item.statusLabel,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [connections, searchQuery]);

  const filteredConnections = useMemo(() => {
    if (filterKey === "all") return searchedConnections;
    return searchedConnections.filter((item) => {
      if (filterKey === "active_sprint") return item.hasActiveSprint;
      if (filterKey === "active_week") return item.activeDaysThisWeek > 0;
      if (filterKey === "idle") return !item.hasActiveSprint && item.statusKey === "idle";
      return true;
    });
  }, [filterKey, searchedConnections]);

  const displayedConnections = useMemo(() => {
    const next = [...filteredConnections];
    next.sort((left, right) => {
      if (sortKey === "name") {
        return String(left.name || "").localeCompare(String(right.name || ""));
      }
      if (sortKey === "active_days") {
        if (right.activeDaysThisWeek !== left.activeDaysThisWeek) {
          return right.activeDaysThisWeek - left.activeDaysThisWeek;
        }
      } else if (sortKey === "quiet_longest") {
        return (right.daysSinceMove ?? -1) - (left.daysSinceMove ?? -1);
      } else {
        if (left.hasActiveSprint !== right.hasActiveSprint) return left.hasActiveSprint ? -1 : 1;
        const leftRecent = left.daysSinceMove ?? Number.MAX_SAFE_INTEGER;
        const rightRecent = right.daysSinceMove ?? Number.MAX_SAFE_INTEGER;
        if (leftRecent !== rightRecent) return leftRecent - rightRecent;
      }
      if (left.hasActiveSprint !== right.hasActiveSprint) return left.hasActiveSprint ? -1 : 1;
      return String(left.name || "").localeCompare(String(right.name || ""));
    });
    return next;
  }, [filteredConnections, sortKey]);

  const ready = useMemo(
    () => displayedConnections.filter((item) => !item.hasActiveSprint && item.connectionId),
    [displayedConnections]
  );

  const handleTabPress = useCallback(
    (key) => {
      if (key === "circle") {
        setSearchParams({});
        return;
      }
      if (key === "find") {
        setSearchParams({ tab: "find" });
        return;
      }
      if (key === "requests") {
        setSearchParams({ tab: "requests" });
        return;
      }
      if (key === "sprints") {
        setSearchParams({ tab: "sprints" });
        return;
      }
    },
    [setSearchParams]
  );

  const handleMessage = useCallback(
    (connection) => {
      const route = buildMessagePath(connection);
      navigate(route || "/activity?tab=messages");
    },
    [navigate]
  );

  const handleOpenProfile = useCallback(
    (connection) => {
      if (!connection?.connectionId) return;
      navigate(`/network/person/${connection.connectionId}?source=connections`);
    },
    [navigate]
  );

  const handleOpenSprint = useCallback(
    (connection) => {
      navigate(
        getSprintWorkspacePath({
          sprintId: connection.sprintId || connection.activeSprint?.id,
          connectionId: connection.connectionId,
          userId: connection.userId,
          focus: connection.needsRenewal ? "details" : undefined,
        })
      );
    },
    [navigate]
  );

  const handleStartSprint = useCallback(
    async (connection, sprintMode = "support", durationDays = 7, focusOverride = null) => {
      if (!connection?.connectionId) return;
      if (connection?.activeSprint?.id || connection?.sprintId) {
        handleOpenSprint(connection);
        return;
      }
      const challengeFocus = focusOverride || (await chooseChallengeFocus(connection));
      if (!challengeFocus) return;
      if (!confirmChallengeRequest(connection, sprintMode, durationDays, challengeFocus)) return;

      try {
        setStartingConnectionId(connection.connectionId);
        setPendingChallengeMode(null);
        setPendingChallengeDuration(7);
        const result = await startSprint(connection.connectionId, {
          userId: connection.userId,
          focus: "chat",
          source: "connections",
          sprintMode,
          durationDays,
          challengeFocus,
        });
        await loadData();
        if (result?.code === "already_active") {
          window.alert(`You're already in Accountability with ${connection.name}.`);
        }
        if (result?.code === "request_sent" || result?.code === "request_pending") {
          setChallengeStyleOpen(false);
          window.alert(`${connection.name} can accept your request to start Accountability.`);
          return;
        }
        if (result?.route) navigate(result.route);
      } catch (error) {
        const activeSprintId = error?.errors?.active_sprint_id || error?.errors?.sprint_id || null;
        if (activeSprintId) {
          navigate(
            getSprintWorkspacePath({
              sprintId: activeSprintId,
              connectionId: connection.connectionId,
              userId: connection.userId,
              focus: "chat",
            })
          );
          return;
        }
        window.alert(error?.message || "Unable to start Accountability right now.");
      } finally {
        setStartingConnectionId(null);
      }
    },
    [handleOpenSprint, loadData, navigate]
  );

  const handleStartChallengeFlow = useCallback(() => {
    setChallengeStyleOpen(true);
  }, []);


  const handleReviewSprint = useCallback((connection) => {
    setReviewConnection(connection);
    setReviewRating("");
    setReviewError("");
  }, []);

  const handleCloseReview = useCallback(() => {
    if (reviewSaving) return;
    setReviewConnection(null);
    setReviewRating("");
    setReviewError("");
  }, [reviewSaving]);

  const handleSaveReview = useCallback(async () => {
    if (!reviewConnection?.connectionId) return;
    if (!reviewRating) {
      setReviewError("Choose a rating before saving.");
      return;
    }

    setReviewSaving(true);
    setReviewError("");
    try {
      await apiFetch(`/connections/${reviewConnection.connectionId}/review/`, {
        method: "POST",
        body: JSON.stringify({
          rating: reviewRating,
          sprint_id: reviewConnection.latestSprint?.id || reviewConnection.activeSprint?.id || null,
        }),
      });
      setReviewConnection(null);
      setReviewRating("");
      await loadData();
    } catch (error) {
      setReviewError(error?.message || "Unable to save review.");
    } finally {
      setReviewSaving(false);
    }
  }, [loadData, reviewConnection, reviewRating]);

  const handleRespondRequest = useCallback(
    async (request, action) => {
      let body = null;
      if (action === "accepted" && request?.request_type === "challenge") {
        const focus = await chooseChallengeFocus({
          goalTitle: request?.challenge_focus?.display_label || "General consistency",
          goalSubtitle: "",
        });
        if (!focus) return;
        body = { challenge_focus: focus };
      }
      const endpoint =
        action === "accepted"
          ? `/connections/${request.id}/accept/`
          : `/connections/${request.id}/decline/`;
      await apiFetch(endpoint, { method: "POST", body: body ? JSON.stringify(body) : undefined });
      await Promise.all([loadRequests(), loadData()]);
    },
    [loadData, loadRequests]
  );

  const handleCancelRequest = useCallback(
    async (requestId) => {
      await apiFetch(`/connections/${requestId}/cancel/`, { method: "POST" });
      await loadRequests();
    },
    [loadRequests]
  );

  const selectedFilterLabel = FILTER_OPTIONS.find((option) => option.key === filterKey)?.label || "All";
  const selectedSortLabel = SORT_OPTIONS.find((option) => option.key === sortKey)?.label || "Recently active";
  const openRequestsCount = incomingRequests.length + outgoingRequests.length;

  return (
    <Box sx={{ maxWidth: 1264 }}>
      {activeTab === "circle" ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr auto", xl: "1fr auto auto" },
            gap: 2,
            alignItems: "start",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: { xs: "2.4rem", md: "3rem" }, lineHeight: 1.04, letterSpacing: 0, fontWeight: 850, color: "#102033" }}>
              Connections
            </Typography>
            <Typography sx={{ mt: 1, fontSize: "1.1rem", color: "#445776", lineHeight: 1.4 }}>
              The people you can message, check in with, or start Accountability with.
            </Typography>
          </Box>

          <ButtonBase
            onClick={() => window.dispatchEvent(new Event("trustbud:open-invite"))}
            sx={{
              minWidth: 194,
              minHeight: 42,
              px: 2,
              borderRadius: "8px",
              border: "1px solid rgba(95, 108, 255, 0.18)",
              backgroundColor: "#fff",
              color: trustBudTokens.colors.brand.navy700,
              justifyContent: "center",
              gap: 0.9,
              fontSize: "0.98rem",
              fontWeight: 700,
              justifySelf: { xs: "stretch", md: "start", xl: "auto" },
            }}
          >
            <PersonAddAlt1RoundedIcon sx={{ fontSize: 20 }} />
            Invite someone
          </ButtonBase>

          <Box
            sx={cardShellSx({
              height: 42,
              px: 1.55,
              display: "grid",
              gridTemplateColumns: "20px 1fr",
              alignItems: "center",
              gap: 1.05,
              minWidth: { xs: 0, xl: 282 },
              width: { xs: "100%", md: 320, xl: 282 },
              gridColumn: { xs: "1 / -1", md: "1 / -1", xl: "auto" },
            })}
          >
            <SearchRoundedIcon sx={{ fontSize: 20, color: "#60738c" }} />
            <InputBase
              placeholder="Search connections..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              sx={{
                fontSize: "0.98rem",
                color: "#1b2843",
                "& input::placeholder": {
                  color: "#60738c",
                  opacity: 1,
                },
              }}
            />
          </Box>
        </Box>
      ) : null}

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 3.2,
          borderBottom: "1px solid rgba(226, 233, 241, 1)",
          mt: 3,
          mb: 2,
          overflowX: "auto",
          whiteSpace: "nowrap",
          pb: 0.35,
        }}
        >
        {TAB_ITEMS.map((item) => {
          const active = item.key === activeTab;
          const requestCount = item.key === "requests" ? openRequestsCount : 0;
          return (
            <Box
              key={item.key}
              onClick={() => handleTabPress(item.key)}
              sx={{
                pb: 1.05,
                px: 0.2,
                borderBottom: active ? `2px solid ${trustBudTokens.colors.brand.navy700}` : "2px solid transparent",
                color: active ? trustBudTokens.colors.brand.navy700 : "#445776",
                fontWeight: active ? 700 : 500,
                fontSize: "1rem",
                whiteSpace: "nowrap",
                flexShrink: 0,
                cursor: active ? "default" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.65,
              }}
            >
              {requestCount > 0 ? <NotificationsNoneRoundedIcon sx={{ fontSize: 18 }} /> : null}
              <span>{item.label}</span>
              {requestCount > 0 ? (
                <Box
                  component="span"
                  sx={{
                    minWidth: 19,
                    height: 19,
                    px: 0.6,
                    borderRadius: 99,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: active ? trustBudTokens.colors.brand.navy700 : "#dbeafe",
                    color: active ? "#fff" : trustBudTokens.colors.brand.navy700,
                    fontSize: "0.72rem",
                    fontWeight: 800,
                    lineHeight: 1,
                  }}
                >
                  {requestCount > 9 ? "9+" : requestCount}
                </Box>
              ) : null}
            </Box>
          );
        })}
      </Box>

      {activeTab === "find" ? (
        <FindPeopleTab
          onOpenInvite={() => window.dispatchEvent(new Event("trustbud:open-invite"))}
          onRequestSent={loadRequests}
        />
      ) : activeTab === "requests" ? (
        <RequestsTab
          incoming={incomingRequests}
          outgoing={outgoingRequests}
          loading={requestsLoading}
          onRespond={handleRespondRequest}
          onCancel={handleCancelRequest}
        />
      ) : activeTab === "sprints" ? (
        <SprintsTab
          connections={connections}
          loading={loading}
          onStartSprint={handleStartSprint}
          onOpenSprint={handleOpenSprint}
          onMessage={handleMessage}
          onOpenProfile={handleOpenProfile}
          onReview={handleReviewSprint}
          onOpenCircle={() => setSearchParams({})}
          onStartChallengeFlow={handleStartChallengeFlow}
          onFindPeople={() => setSearchParams({ tab: "find" })}
        />
      ) : (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 262px" },
          gap: 2.1,
          alignItems: "start",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={cardShellSx({ p: { xs: 1.45, sm: 2.1 }, overflow: "hidden" })}>
            {pendingChallengeMode ? (
              <Box
                sx={{
                  mb: 1.7,
                  p: 1.65,
                  borderRadius: "8px",
                  backgroundColor: "#f3f8ff",
                  border: "1px solid rgba(95, 108, 255, 0.16)",
                }}
              >
                <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: "#13233d" }}>
                  Next, choose the TrustBud who should receive the Accountability request for {pendingChallengeDuration} days.
                </Typography>
                <Typography sx={{ mt: 0.35, color: "#50647e" }}>
                  Pick someone below. The other person will see the focus and duration before accepting.
                </Typography>
              </Box>
            ) : null}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
                gap: 2,
                alignItems: "center",
                mb: 0.55,
              }}
            >
              <Typography sx={{ fontSize: "1.35rem", fontWeight: 850, color: "#102033", letterSpacing: 0 }}>
                The people around you
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2.1, color: "#1b2843", flexWrap: "wrap" }}>
                <ButtonBase
                  onClick={(event) => setFilterAnchor(event.currentTarget)}
                  sx={{ display: "flex", alignItems: "center", gap: 0.45, fontSize: "0.98rem", fontWeight: 650, borderRadius: "8px", px: 0.5, py: 0.25 }}
                >
                  Filter: {selectedFilterLabel}
                  <FilterListRoundedIcon sx={{ fontSize: 18, color: "#556983" }} />
                </ButtonBase>
                <ButtonBase
                  onClick={(event) => setSortAnchor(event.currentTarget)}
                  sx={{ display: "flex", alignItems: "center", gap: 0.25, fontSize: "0.98rem", fontWeight: 650, borderRadius: "8px", px: 0.5, py: 0.25 }}
                >
                  Sort: {selectedSortLabel}
                  <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18, color: "#556983" }} />
                </ButtonBase>
              </Box>
            </Box>

            <Box sx={{ mt: 0.35, minWidth: 0 }}>
              {loading ? (
                <LoadingState
                  title="Preparing Connections"
                  body="Checking TrustBuds, requests, and Accountability options."
                  rows={4}
                  compact
                  sx={{ my: 1 }}
                />
              ) : displayedConnections.length === 0 ? (
                <Typography sx={{ py: 2.2, color: "#576b84", fontSize: "1rem" }}>
                  No one matches this view.
                </Typography>
              ) : (
                displayedConnections.map((connection) => (
                  <ConnectionsRow
                    key={connection.id}
                    item={connection}
                    onOpenSprint={handleOpenSprint}
                    onMessage={handleMessage}
                    onOpenProfile={handleOpenProfile}
                    onStartSprint={handleStartSprint}
                    pendingChallengeMode={pendingChallengeMode}
                    pendingChallengeDuration={pendingChallengeDuration}
                  />
                ))
              )}
            </Box>
          </Box>

          <Menu anchorEl={filterAnchor} open={Boolean(filterAnchor)} onClose={() => setFilterAnchor(null)}>
            {FILTER_OPTIONS.map((option) => (
              <MenuItem
                key={option.key}
                selected={option.key === filterKey}
                onClick={() => {
                  setFilterKey(option.key);
                  setFilterAnchor(null);
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>

          <Menu anchorEl={sortAnchor} open={Boolean(sortAnchor)} onClose={() => setSortAnchor(null)}>
            {SORT_OPTIONS.map((option) => (
              <MenuItem
                key={option.key}
                selected={option.key === sortKey}
                onClick={() => {
                  setSortKey(option.key);
                  setSortAnchor(null);
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>

          <Box
            sx={cardShellSx({
              mt: 1.9,
              px: 2.2,
              py: 1.75,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "64px 1fr auto" },
              gap: 1.4,
              alignItems: { xs: "stretch", sm: "center" },
              background: "linear-gradient(180deg, #f3f8ff 0%, #f7fbff 100%)",
            })}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                backgroundColor: "#e7f0ff",
                color: trustBudTokens.colors.brand.navy700,
              }}
            >
              <PeopleAltOutlinedIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "1.7rem", fontWeight: 850, letterSpacing: 0, color: "#102033" }}>
                Grow your network
              </Typography>
              <Typography sx={{ mt: 0.35, fontSize: "1rem", color: "#50647e" }}>
                More meaningful connections lead to more consistency.
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => navigate("/connections?tab=find")}
              sx={{
                minWidth: { xs: 0, sm: 204 },
                width: { xs: "100%", sm: "auto" },
                minHeight: 42,
                borderRadius: "8px",
                backgroundColor: trustBudTokens.colors.brand.navy700,
                fontSize: "1rem",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: trustBudTokens.colors.brand.navy800,
                },
              }}
            >
              Find people to connect
            </Button>
          </Box>
        </Box>

        <Box sx={{ minWidth: 0, display: "grid", gap: 1.55 }}>
          <SideNoteCard
            icon={<LightbulbOutlinedIcon sx={{ fontSize: 23 }} />}
            title="Tips"
            body="When someone goes quiet, send one clear note instead of waiting for the board to fix it."
            cta="See tips"
            onClick={() => navigate("/how-trustbud-works")}
          />
          <SideNoteCard
            icon={<PersonAddAlt1RoundedIcon sx={{ fontSize: 23 }} />}
            title="Invite a friend"
            body="Invite the person who will notice when you disappear, not just someone who likes the goal."
            cta="Invite someone"
            onClick={() => window.dispatchEvent(new Event("trustbud:open-invite"))}
          />
        </Box>
      </Box>
      )}
      <SprintReviewDialog
        connection={reviewConnection}
        rating={reviewRating}
        saving={reviewSaving}
        error={reviewError}
        onSelectRating={setReviewRating}
        onSubmit={handleSaveReview}
        onClose={handleCloseReview}
      />
      <ChallengeStyleDialog
        open={challengeStyleOpen}
        onClose={() => setChallengeStyleOpen(false)}
        connections={ready}
        sendingConnectionId={startingConnectionId}
        onSend={handleStartSprint}
      />
    </Box>
  );
}
